/**
 * Resolução da estratégia de gravação de áudio (recursos m4a-recording e
 * nota-de-voz).
 *
 * São DOIS alvos, e a diferença não é estética:
 *   - `opus` (WhatsApp Cloud): o container decide a renderização. Só Ogg/Opus
 *     vira NOTA DE VOZ; m4a chega como anexo de áudio, com ícone de música.
 *     Chrome/Edge só gravam WebM/Opus — o servidor remuxa para Ogg (mesmos
 *     pacotes, sem reencode). Sem Opus no navegador (Safari), cai no alvo m4a:
 *     entrega o áudio, só não como nota de voz.
 *   - `m4a` (Instagram / Messenger / Instagram Login): é o único formato que
 *     essas APIs tocam, e não há API de browser que grave M4A em todo lugar.
 *
 * Estratégias:
 *   - native-mp4   : MediaRecorder nativo `audio/mp4;codecs=mp4a.40.2` (Path A)
 *                    — Safari sempre, Chromium quando o SO tem encoder AAC. O
 *                    codec vai explícito: `audio/mp4` puro deixa o Chrome
 *                    escolher Opus, que os apps da Meta tocam sem som.
 *   - webcodecs    : Path B — captura PCM + AudioEncoder (mp4a.40.2) + mp4-muxer.
 *   - wasm         : fallback Path B — encoder AAC em WASM (Firefox; Chromium sem encoder).
 *   - native-ogg-opus : MediaRecorder `audio/ogg;codecs=opus` (Firefox) — já é o
 *                    formato final, nada a remuxar.
 *   - native-webm-opus: MediaRecorder `audio/webm;codecs=opus` (Chrome/Edge) —
 *                    mesmos pacotes Opus, remuxados para Ogg no servidor.
 *   - native-webm  : plataformas não-Meta sem mp4 nativo (comportamento legado).
 *   - ogg-evolution: comportamento antigo (WebM/Opus → remux OGG no servidor),
 *                    mantido só como escape de debug.
 *
 * NUNCA decidir por user-agent — o suporte a AAC do Chromium é por máquina.
 */

export type AudioPath =
  | 'native-ogg-opus'
  | 'native-webm-opus'
  | 'native-mp4'
  | 'native-webm'
  | 'webcodecs'
  | 'wasm'
  | 'ogg-evolution';

/** O que o canal pede do gravador (ver `formatoDeAudioDoCanal`). */
export type FormatoDeAudio = 'opus' | 'm4a' | 'livre';

/** Força de caminho para debug. `auto` = detecção em runtime. */
export type AudioPathOverride =
  | 'auto'
  | 'path-a'        // força native-mp4
  | 'path-b'        // força webcodecs
  | 'fallback'      // força wasm
  | 'ogg-evolution';

const OVERRIDE_KEY = 'whiz.audioPathOverride';
const VALID_OVERRIDES: AudioPathOverride[] = ['auto', 'path-a', 'path-b', 'fallback', 'ogg-evolution'];

/**
 * Lê o override de debug. Prioridade: env de build (`VITE_AUDIO_PATH`) →
 * localStorage (`whiz.audioPathOverride`, ajustável em runtime no console) → auto.
 */
export function getAudioPathOverride(): AudioPathOverride {
    const env = (import.meta.env as Record<string, string | undefined>).VITE_AUDIO_PATH?.trim();
    let ls: string | null = null;
    try { ls = typeof localStorage !== 'undefined' ? localStorage.getItem(OVERRIDE_KEY) : null; } catch { /* SSR / bloqueio */ }
    const v = (env || ls || 'auto') as AudioPathOverride;
    return VALID_OVERRIDES.includes(v) ? v : 'auto';
}

/**
 * Mimetype pedido ao MediaRecorder no Path A. O codec é EXPLÍCITO de propósito:
 * `audio/mp4` sozinho não diz nada sobre a faixa, e o Chrome em máquina sem
 * encoder AAC do sistema aceita `audio/mp4` e entrega **Opus dentro do MP4** —
 * arquivo que a Meta repassa sem transcodificar e que toca MUDO no aplicativo
 * do celular. `mp4a.40.2` é AAC-LC.
 */
export const MIME_M4A_AAC = 'audio/mp4;codecs=mp4a.40.2';

/**
 * Path A só vale quando o navegador confirma AAC-LC em MP4. Com o teste antigo
 * (`isTypeSupported('audio/mp4')`) o Chrome dizia sim, gravava Opus em MP4 e o
 * áudio chegava sem som no Instagram — era essa a causa do "áudio mudo".
 */
export function supportsNativeM4a(): boolean {
    return typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(MIME_M4A_AAC);
}

/** Mimetypes de gravação em Opus, em ordem de preferência: o Ogg já é o formato
 *  final que o WhatsApp quer; o WebM precisa do remux no servidor. */
export const MIME_OGG_OPUS = 'audio/ogg;codecs=opus';
export const MIME_WEBM_OPUS = 'audio/webm;codecs=opus';

/** WebCodecs AAC-LC (mp4a.40.2). Tipos via @types/dom-webcodecs; runtime pode não ter. */
export async function supportsWebCodecsAac(sampleRate = 48000): Promise<boolean> {
    if (typeof AudioEncoder === 'undefined') return false;
    try {
        const s = await AudioEncoder.isConfigSupported({ codec: 'mp4a.40.2', sampleRate, numberOfChannels: 1, bitrate: 64000 });
        return !!s.supported;
    } catch {
        return false;
    }
}

/**
 * Decide a estratégia de gravação a partir do que o canal pede.
 *
 * `opus` tenta Opus primeiro (Ogg nativo → WebM nativo) e só então cai na
 * cadeia m4a — degradar para anexo de áudio é melhor que travar a gravação num
 * navegador sem Opus.
 */
export async function resolveAudioPath(formato: FormatoDeAudio, sampleRate?: number): Promise<AudioPath> {
    switch (getAudioPathOverride()) {
        case 'ogg-evolution': return 'ogg-evolution';
        case 'path-a':        return 'native-mp4';
        case 'path-b':        return 'webcodecs';
        case 'fallback':      return 'wasm';
        case 'auto':
        default:
            break;
    }

    if (formato === 'opus' && typeof MediaRecorder !== 'undefined') {
        if (MediaRecorder.isTypeSupported(MIME_OGG_OPUS)) return 'native-ogg-opus';
        if (MediaRecorder.isTypeSupported(MIME_WEBM_OPUS)) return 'native-webm-opus';
        // Safari e afins: sem Opus, segue para a cadeia m4a abaixo.
    }

    if (supportsNativeM4a()) return 'native-mp4';

    if (formato !== 'livre') {
        if (await supportsWebCodecsAac(sampleRate)) return 'webcodecs';
        return 'wasm';
    }

    // Plataforma não-Meta sem mp4 nativo: mantém WebM/Opus (legado).
    return 'native-webm';
}

/** `true` quando o caminho entrega Opus (Ogg ou WebM) — ou seja, nota de voz. */
export function pathEhOpus(path: AudioPath): boolean {
    return path === 'native-ogg-opus' || path === 'native-webm-opus';
}

/** Mimetype do MediaRecorder por caminho nativo. */
export function nativeMimeForPath(path: AudioPath): string {
    // Com o codec explícito: sem ele o Chrome escolhe Opus quando não tem
    // encoder AAC, e o áudio sai mudo nos apps da Meta.
    if (path === 'native-mp4') {
        return MediaRecorder.isTypeSupported(MIME_M4A_AAC) ? MIME_M4A_AAC : 'audio/mp4';
    }
    if (path === 'native-ogg-opus') return MIME_OGG_OPUS;
    if (path === 'native-webm-opus') return MIME_WEBM_OPUS;
    if (path === 'ogg-evolution') {
        return MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
    }
    // native-webm
    if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) return 'audio/webm;codecs=opus';
    return 'audio/webm';
}
